import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { amountToWordsSoles } from './amountToWords';
import { getItemPrintLineDisplay } from './unitSelectUtils';
import { formatLinkedRequirementCodes } from './purchaseOrderRequirements';

const TEAL = [13, 148, 136];
const GRAY = [55, 65, 81];
const GRAY_MUTED = [107, 114, 128];
const GRAY_LIGHT = [156, 163, 175];
const BG_TABLE_ALT = [249, 250, 251];
const MARGIN = 10;
const HEADER_FONT = 7.5;

const fmtMoney = (n) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtUnitPrice = (p) =>
  parseFloat(p || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleDateString('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function buildPaymentCondition(order) {
  if (order.payment_condition_type === 'cash') return 'Al contado';
  if (order.payment_condition_type === 'days') {
    return `Cada ${order.payment_condition_days} días (máx. 3 meses)`;
  }
  if (order.payment_condition_type === 'date' && order.payment_condition_date) {
    return `Fecha específica: ${formatDate(order.payment_condition_date)}`;
  }
  return '-';
}

function buildPaymentDate(order) {
  if (order.payment_condition_type === 'cash') return '';
  if (order.payment_condition_type === 'date' && order.payment_condition_date) {
    return formatDate(order.payment_condition_date);
  }
  if (order.payment_condition_type === 'days' && order.payment_condition_days && order.order_date) {
    const d = new Date(order.order_date);
    d.setDate(d.getDate() + parseInt(order.payment_condition_days, 10));
    return formatDate(d);
  }
  return '';
}

function docTypeLabel(supplier) {
  if (supplier?.document_type === 'ruc') return 'RUC';
  if (supplier?.document_type === 'dni') return 'DNI';
  if (supplier?.document_type) return String(supplier.document_type).toUpperCase();
  return 'Documento';
}

function measureSegments(doc, segments) {
  let width = 0;
  segments.forEach((seg) => {
    doc.setFont('helvetica', seg.style === 'label' ? 'bold' : 'normal');
    doc.setFontSize(HEADER_FONT);
    width += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
  return width;
}

function drawSegments(doc, startX, y, segments) {
  let x = startX;
  segments.forEach((seg) => {
    if (seg.style === 'label') {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...TEAL);
    } else if (seg.style === 'sep') {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY_LIGHT);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY);
    }
    doc.setFontSize(HEADER_FONT);
    doc.text(seg.text, x, y);
    x += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
  return x;
}

function drawStatusBadge(doc, x, y, statusText, isIssued) {
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  const padX = 2;
  const textW = doc.getTextWidth(statusText);
  const badgeW = textW + padX * 2;
  const badgeH = 3.8;

  if (isIssued) {
    doc.setFillColor(209, 250, 229);
    doc.setTextColor(6, 95, 70);
  } else {
    doc.setFillColor(254, 226, 226);
    doc.setTextColor(153, 27, 27);
  }
  doc.roundedRect(x, y - badgeH + 1.2, badgeW, badgeH, 1.2, 1.2, 'F');
  doc.text(statusText, x + padX, y);
  return x + badgeW + 2;
}

function drawSectionTitle(doc, margin, y, title) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...TEAL);
  doc.text(title.toUpperCase(), margin, y);
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.3);
  doc.line(margin, y + 1.2, margin + doc.getTextWidth(title.toUpperCase()), y + 1.2);
}

function buildTableRows(order, productConversionsEnabled) {
  const useConverted = order.use_converted_quantity_for_subtotal !== false;
  return (order.items || []).map((item) => {
    const line = getItemPrintLineDisplay({
      item,
      productConversionsEnabled,
      useConvertedQuantityForSubtotal: useConverted,
    });
    return [
      item.product?.name || 'N/A',
      line.unit,
      item.project ? `${item.project.code} - ${item.project.name}` : '-',
      line.quantity,
      `S/ ${fmtUnitPrice(item.unit_price)}`,
      `S/ ${fmtMoney(line.subtotal)}`,
    ];
  });
}

function getAmountsRightX(doc, pageWidth) {
  const table = doc.lastAutoTable;
  const subtotalCol = table?.table?.columns?.[5];
  if (subtotalCol) {
    return subtotalCol.x + subtotalCol.width - 3;
  }
  return MARGIN + 66 + 16 + 42 + 16 + 24 + 26 - 3;
}

function drawFooter(doc, order, startY, pageWidth, pageHeight, amountsRightX) {
  const contentWidth = pageWidth - MARGIN * 2;
  const obsWidth = contentWidth * 0.52;
  const totalsWidth = contentWidth * 0.44;
  const gap = contentWidth - obsWidth - totalsWidth;
  const footerHeight = 22;

  let y = startY;
  if (y + footerHeight > pageHeight - MARGIN) {
    doc.addPage();
    y = MARGIN + 4;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...TEAL);
  doc.text('Observaciones:', MARGIN, y + 4);

  if (order.notes) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    const noteLines = doc.splitTextToSize(String(order.notes), obsWidth);
    doc.text(noteLines.slice(0, 4), MARGIN, y + 8);
  }

  const totalsX = MARGIN + obsWidth + gap;

  const igvRate = parseFloat(order.igv_rate ?? 18);
  let ty = y + 4;
  const lineH = 3.8;

  const drawTotalLine = (label, value, { bold = false, italic = false } = {}) => {
    doc.setFont('helvetica', bold ? 'bold' : italic ? 'italic' : 'normal');
    doc.setFontSize(bold ? 8 : 7);
    doc.setTextColor(...(bold ? TEAL : GRAY_MUTED));
    doc.text(label, totalsX, ty);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setTextColor(...GRAY);
    doc.text(value, amountsRightX, ty, { align: 'right' });
    ty += lineH;
  };

  drawTotalLine('Base:', `S/ ${fmtMoney(order.base_imponible)}`);
  drawTotalLine(`IGV (${igvRate}%):`, `S/ ${fmtMoney(order.igv_amount)}`);
  if (parseFloat(order.no_gravados || 0) > 0) {
    drawTotalLine('No grav.:', `S/ ${fmtMoney(order.no_gravados)}`);
  }
  drawTotalLine('Total:', `S/ ${fmtMoney(order.total_amount)}`, { bold: true });

  const words = amountToWordsSoles(order.total_amount);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(6.5);
  doc.setTextColor(...GRAY_MUTED);
  const wordLines = doc.splitTextToSize(`Son: ${words}`, amountsRightX - totalsX);
  doc.text(wordLines.slice(0, 2), amountsRightX, ty + 1, { align: 'right' });
}

export async function downloadPurchaseOrderPdf(order, { productConversionsEnabled = true } = {}) {
  if (!order) {
    throw new Error('No hay datos de la orden para generar el PDF');
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  let y = MARGIN + 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...TEAL);
  doc.text('ORDEN DE COMPRA', pageWidth / 2, y, { align: 'center' });

  y += 2.5;
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 5;

  const orderDate = formatDate(order.order_date);
  const deliveryDate = formatDate(order.expected_delivery_date);
  const statusText = order.status === 'issued' ? 'Emitida' : 'Cancelada';
  const isIssued = order.status === 'issued';

  const line1 = [
    { text: 'Orden:', style: 'label' },
    { text: order.code || '-', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Fecha:', style: 'label' },
    { text: orderDate, style: 'value' },
  ];
  if (deliveryDate) {
    line1.push(
      { text: '·', style: 'sep' },
      { text: 'Entrega:', style: 'label' },
      { text: deliveryDate, style: 'value' },
    );
  }
  line1.push({ text: '·', style: 'sep' }, { text: 'Estado:', style: 'label' });
  const estadoX = MARGIN + measureSegments(doc, line1);
  drawSegments(doc, MARGIN, y, line1);
  drawStatusBadge(doc, estadoX, y, statusText, isIssued);

  y += 4;
  const supplierLine = [
    { text: 'Proveedor:', style: 'label' },
    { text: order.supplier?.name || 'N/A', style: 'value' },
  ];
  if (order.supplier?.document_number) {
    supplierLine.push(
      { text: '·', style: 'sep' },
      { text: `${docTypeLabel(order.supplier)}:`, style: 'label' },
      { text: order.supplier.document_number, style: 'value' },
    );
  }
  if (order.supplier?.address) {
    supplierLine.push(
      { text: '·', style: 'sep' },
      { text: 'Dir.:', style: 'label' },
      { text: order.supplier.address, style: 'value' },
    );
  }
  drawSegments(doc, MARGIN, y, supplierLine);

  y += 4;
  const paymentLine = [
    { text: 'Condición de pago:', style: 'label' },
    { text: buildPaymentCondition(order), style: 'value' },
  ];
  const paymentDate = buildPaymentDate(order);
  if (paymentDate) {
    paymentLine.push(
      { text: '·', style: 'sep' },
      { text: 'Fecha de pago:', style: 'label' },
      { text: paymentDate, style: 'value' },
    );
  }
  drawSegments(doc, MARGIN, y, paymentLine);

  const requirementCodes = formatLinkedRequirementCodes(order);
  if (requirementCodes) {
    y += 4;
    const requirementLine = [
      {
        text: requirementCodes.includes(',') ? 'Requerimientos:' : 'Requerimiento:',
        style: 'label',
      },
      { text: requirementCodes, style: 'value' },
    ];
    drawSegments(doc, MARGIN, y, requirementLine);
  }

  y += 6;
  drawSectionTitle(doc, MARGIN, y, 'Items de la Orden');
  y += 4;

  const tableBody = buildTableRows(order, productConversionsEnabled);

  autoTable(doc, {
    startY: y,
    head: [['Descripción', 'Unidad', 'Proyecto', 'Cant.', 'P. Unit', 'Subtotal']],
    body: tableBody,
    margin: { left: MARGIN, right: MARGIN },
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 6,
      cellPadding: { top: 1, right: 2, bottom: 1, left: 2 },
      minCellHeight: 3.8,
      textColor: GRAY,
      lineColor: [229, 231, 235],
      lineWidth: 0.2,
      valign: 'middle',
    },
    headStyles: {
      fillColor: TEAL,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 6,
      halign: 'left',
    },
    columnStyles: {
      0: { cellWidth: 66 },
      1: { cellWidth: 16, halign: 'center' },
      2: { cellWidth: 42 },
      3: { cellWidth: 16, halign: 'right' },
      4: { cellWidth: 24, halign: 'right' },
      5: { cellWidth: 26, halign: 'right', fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: BG_TABLE_ALT },
    didDrawPage: (data) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...GRAY_LIGHT);
      doc.text(
        `Orden ${order.code || ''} — Pág. ${data.pageNumber}`,
        pageWidth / 2,
        pageHeight - 6,
        { align: 'center' },
      );
    },
  });

  const tableEndY = doc.lastAutoTable.finalY + 5;
  const amountsRightX = getAmountsRightX(doc, pageWidth);
  drawFooter(doc, order, tableEndY, pageWidth, pageHeight, amountsRightX);

  const safeCode = String(order.code || order.id || 'orden').replace(/[^\w.-]+/g, '_');
  doc.save(`Orden_Compra_${safeCode}.pdf`);
}
